import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_BODY_SM } from "oolib/dist/v2/components/Typo2";
import { SealWarning } from "@phosphor-icons/react";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { PillDropdown } from "../../comps/PillDropdown";
import { useLensesV2Context } from "../LensesV2Context";
import { useAggrMetricsAcrossDimensions } from "@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { TrendPill } from "../comps/TrendPill";
import { toTagCountMap } from "../utils";
import { assembleIssues, buildIssueFindQuery } from "./utils";
import { CONFIG } from "./CONFIG";

const ALL_CATEGORIES = { value: "", display: "All Topic Categories" };

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const IssueList = styled.div`
  display: flex;
  flex-direction: column;
`;

const IssueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors2.grey5};

  &:last-child {
    border-bottom: none;
  }
`;

const IssueNumber = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors2.grey10};
  color: ${colors2.grey70};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IssueLabel = styled.div`
  flex: 1;
  min-width: 0;
  color: ${colors2.grey80};
`;

const IssueRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

// Issues widget — topics ranked by how many DISTINCT dissatisfaction ("issue")
// annotations mention them, grouped by topic subTheme. Same v1 TeamLensBlock UI
// (numbered rows, count, trend pill, "Issues in <topic category>" dropdown), minus
// the drilldown sidebar. Single source: a per-period issue-filtered aggrMetrics
// over the topics dimension (annotations restricted to those carrying a
// dissatisfaction signal). Exact per-annotation counts — no cooccurrence, no
// signal-weighted over-count. Topic subTheme rides on context.topics.
export const IssuesWidget = () => {
  const { findQuery, prevFindQuery, signals, topics } = useLensesV2Context();
  const [selectedCategory, setSelectedCategory] = useState(null);

  const currIssueQuery = useMemo(() => buildIssueFindQuery(findQuery, signals), [findQuery, signals]);
  const prevIssueQuery = useMemo(() => buildIssueFindQuery(prevFindQuery, signals), [prevFindQuery, signals]);

  const currCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery: currIssueQuery,
    vizCardId: "lensV2IssueCurr",
    queryOptions: { enabled: !!currIssueQuery },
  });

  const prevCounts = useAggrMetricsAcrossDimensions({
    contentType: "annotations",
    dimensions: ["annotations.tags.tags"],
    findQuery: prevIssueQuery,
    vizCardId: "lensV2IssuePrev",
    queryOptions: { enabled: !!prevIssueQuery },
  });

  const { issues, subThemes } = useMemo(
    () => assembleIssues(topics, toTagCountMap(currCounts.data), toTagCountMap(prevCounts.data)),
    [topics, currCounts.data, prevCounts.data]
  );

  // Only show the dropdown when topics span more than one subTheme; otherwise
  // it's just "Top Issues" (a single category needs no picker).
  const hasDropdown = subThemes.length > 1;
  const filtered =
    hasDropdown && selectedCategory?.value
      ? issues.filter((i) => i.subThemeId === selectedCategory.value)
      : issues;

  return (
    <DashboardWidgetShell
      titleJSX={
        <TitleWrapper>
          <SealWarning size={16} color={colors2.grey80} weight="bold" />
          <UI_BODY_SEMIBOLD_SM_DF style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {hasDropdown ? (
              <>
                Issues in
                <PillDropdown
                  value={selectedCategory || ALL_CATEGORIES}
                  onChange={(id, val) => setSelectedCategory(val?.value ? val : null)}
                  options={[ALL_CATEGORIES, ...subThemes]}
                />
              </>
            ) : (
              "Top Issues"
            )}
          </UI_BODY_SEMIBOLD_SM_DF>
        </TitleWrapper>
      }
      style={{ gap: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: 520 }}
    >
      {currCounts.isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0" }}>
              <SkeletonLoader style={{ height: 14, width: `${50 + i * 5}%`, borderRadius: 4 }} />
              <SkeletonLoader style={{ height: 22, width: 60, borderRadius: 12 }} />
            </div>
          ))}
        </div>
      )}

      {currCounts.isError && (
        <UI_BODY_SM style={{ color: "#dc2626" }}>
          Error: {currCounts.error?.response?.data?.message || currCounts.error?.message}
        </UI_BODY_SM>
      )}

      {currCounts.data && filtered.length === 0 && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No issue data available yet.</UI_BODY_SM>
      )}

      {currCounts.data && filtered.length > 0 && (
        <IssueList>
          {filtered.slice(0, CONFIG.maxIssues).map((issue, idx) => (
            <IssueRow key={issue.topicId}>
              <IssueNumber>
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey70 }}>{idx + 1}</UI_BODY_SEMIBOLD_SM_DF>
              </IssueNumber>
              <IssueLabel>
                <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>{issue.name}</UI_BODY_SEMIBOLD_SM_DF>
              </IssueLabel>
              <IssueRight>
                <div style={{ minWidth: 60, display: "flex", justifyContent: "center" }}>
                  <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey60 }}>{issue.currentCount}</UI_BODY_SEMIBOLD_SM_DF>
                </div>
                <div style={{ minWidth: 100, display: "flex", justifyContent: "flex-end" }}>
                  {/* rising issues = bad (red), declining = good (green) */}
                  <TrendPill trend={issue.trend} variant="trend" tone="bad" />
                </div>
              </IssueRight>
            </IssueRow>
          ))}
        </IssueList>
      )}
    </DashboardWidgetShell>
  );
};
